import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import type { RealmAdmissionCandidate, RealmMemberDetail, RealmMembershipState } from '@laspalmas-identity/shared';
import type { IdentityTrpcClient } from '../../lib/trpc';
import { navigate } from '../../lib/router';
import { localizedError, useI18n, type MessageKey } from '../../lib/i18n';
import { IdentityCandidateSelector } from '../../components/IdentityCandidateSelector';
import { Badge, Button, Card, DataTable, Dialog, Field, Input, Notice, PageHeader, Select } from '../../components/ui';

type Props = { realmId: string; identityId?: string | undefined; client: IdentityTrpcClient; blocked?: string | undefined };
const stateKeys: Record<RealmMembershipState, MessageKey> = { active: 'adminActive', suspended: 'adminMemberSuspended', revoked: 'adminMemberRevoked' };
const accessKeys: Record<RealmMemberDetail['accessReasons'][number], MessageKey> = {
  membership_suspended: 'adminAccessMembershipSuspended', membership_revoked: 'adminAccessMembershipRevoked',
  identity_suspended: 'adminAccessIdentitySuspended', identity_deleted: 'adminAccessIdentityDeleted',
  realm_suspended: 'adminAccessRealmSuspended', realm_deleted: 'adminAccessRealmDeleted',
  email_unverified: 'adminAccessEmailUnverified', explicit_block: 'adminAccessExplicitBlock',
};
const pageSize = 25;

export function RealmMembersPage({ realmId, identityId, client, blocked }: Props) {
  const base = `/admin/realms/${encodeURIComponent(realmId)}/membros`;
  return identityId
    ? <MemberDetail key={identityId} realmId={realmId} identityId={identityId} client={client} blocked={blocked} base={base}/>
    : <MemberList realmId={realmId} client={client} blocked={blocked} base={base}/>;
}

function MemberList({ realmId, client, blocked, base }: { realmId: string; client: IdentityTrpcClient; blocked?: string | undefined; base: string }) {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [state, setState] = useState<RealmMembershipState | ''>('');
  const [offset, setOffset] = useState(0);
  const [result, setResult] = useState<Awaited<ReturnType<IdentityTrpcClient['admin']['realmMembers']['query']>>>();
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [revision, setRevision] = useState(0);
  const searchCandidates = useCallback((input: { realmId: string; query: string; limit: number }) => client.admin.searchRealmAdmissionCandidates.query({ ...input, purpose: 'member' }), [client]);
  async function add(candidate: RealmAdmissionCandidate) {
    if (blocked || saving) return;
    setError(''); setSaving(true);
    try {
      if (candidate.kind === 'pending_email') await client.admin.addPolicyEmail.mutate({ realmId, email: candidate.email });
      else await client.admin.addFixedIdentity.mutate({ realmId, identityId: candidate.identity.id });
      setOpen(false);
      // A pending eligibility email is an admission rule, not a stored membership.
      if (candidate.kind === 'identity') setRevision((current) => current + 1);
    } catch (reason) { setError(localizedError(reason, t)); }
    finally { setSaving(false); }
  }
  useEffect(() => {
    let active = true;
    setResult(undefined); setError('');
    client.admin.realmMembers.query({ realmId, query, ...(state ? { state } : {}), limit: pageSize, offset })
      .then((response) => { if (active) setResult(response); })
      .catch((reason) => { if (active) setError(localizedError(reason, t)); });
    return () => { active = false; };
  }, [client, realmId, query, state, offset, revision, t]);
  return <>
    <PageHeader eyebrow={t('adminRealmAdministration')} title={t('adminMembers')} description={t('adminMembersRealmDescription')} actions={<Button disabled={Boolean(blocked) || saving} onClick={() => setOpen(true)}>{t('adminAddMember')}</Button>}/>
    {blocked && <Notice tone="warning" title={t('adminReadOnlyMode')}>{blocked}</Notice>}
    <Card className="section-card">
      <div className="toolbar">
        <Field label={t('adminSearchMembers')}><Input type="search" value={query} onChange={(event) => { setQuery(event.target.value); setOffset(0); }}/></Field>
        <Field label={t('adminMembershipFilter')}><Select value={state} onChange={(event) => { setState(event.target.value as RealmMembershipState | ''); setOffset(0); }}><option value="">{t('adminAllMemberships')}</option>{(['active', 'suspended', 'revoked'] as const).map((value) => <option key={value} value={value}>{t(stateKeys[value])}</option>)}</Select></Field>
      </div>
      {error && <Notice tone="danger" title={t('adminLoadMembersFailed')}>{error}</Notice>}
      {!error && !result && <p className="loading-inline"><span className="spinner"/> {t('loading')}</p>}
      {result && <><DataTable label={t('adminMembers')} headers={[t('adminMember'), t('adminStatus'), t('adminMembershipAccess'), t('adminActions')]}>
        {result.members.map((member) => <tr key={member.identity.id}>
          <td><strong>{member.identity.displayName ?? t('adminUnnamed')}</strong><small className="block-code">{member.identity.email}</small></td>
          <td><Badge tone={member.membership.state === 'active' ? 'success' : 'warning'}>{t(stateKeys[member.membership.state])}</Badge></td>
          <td>{member.effectiveAccess ? t('adminMembershipEffectiveAccess') : t('adminMembershipNoEffectiveAccess')}</td>
          <td><a href={`${base}/${encodeURIComponent(member.identity.id)}`} onClick={(event) => { event.preventDefault(); navigate(`${base}/${encodeURIComponent(member.identity.id)}`); }}>{t('adminDetails')}</a></td>
        </tr>)}
        {!result.members.length && <tr><td colSpan={4}>{t('adminNoMatchingMembers')}</td></tr>}
      </DataTable><div className="dialog-actions">
        <span>{t('adminMemberCount', { count: result.total })}</span>
        <Button variant="ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - pageSize))}>{t('adminPreviousMembers')}</Button>
        <Button variant="ghost" disabled={offset + pageSize >= result.total} onClick={() => setOffset(offset + pageSize)}>{t('adminNextMembers')}</Button>
      </div></>}
    </Card>
    <Dialog open={open} title={t('adminAddMember')} description={t('adminAddMemberDescription')} onClose={() => setOpen(false)}>
      <IdentityCandidateSelector label={t('adminSearchMembers')} realmId={realmId} allowPending search={searchCandidates} onSelect={(candidate) => void add(candidate)}/>
      <div className="dialog-actions"><Button variant="ghost" onClick={() => setOpen(false)}>{t('cancel')}</Button></div>
    </Dialog>
  </>;
}

function MemberDetail({ realmId, identityId, client, blocked, base }: Props & { identityId: string; base: string }) {
  const { t } = useI18n();
  const [detail, setDetail] = useState<RealmMemberDetail>();
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [target, setTarget] = useState<RealmMembershipState | ''>('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  useEffect(() => {
    let active = true;
    setDetail(undefined); setError('');
    client.admin.realmMemberDetail.query({ realmId, identityId })
      .then((response) => { if (active) setDetail(response); })
      .catch((failure) => { if (active) setError(localizedError(failure, t)); });
    return () => { active = false; };
  }, [client, realmId, identityId, revision, t]);
  async function save(event: FormEvent) {
    event.preventDefault();
    if (!target || !reason.trim() || blocked || saving) return;
    setError(''); setSuccess(''); setSaving(true);
    try {
      const result = await client.admin.setRealmMembershipState.mutate({ realmId, identityId, state: target, reason: reason.trim() });
      if (!mounted.current) return;
      setSuccess(t('adminMemberStateSaved', { consents: result.revokedConsents, artifacts: result.revokedArtifacts }));
      setTarget(''); setReason(''); setRevision((current) => current + 1);
    } catch (failure) { if (mounted.current) setError(localizedError(failure, t)); }
    finally { if (mounted.current) setSaving(false); }
  }
  return <>
    <PageHeader eyebrow={t('adminRealmAdministration')} title={detail?.identity.displayName ?? t('adminMember')} actions={<a href={base} onClick={(event) => { event.preventDefault(); navigate(base); }}>{t('adminBackToMembers')}</a>}/>
    {error && <Notice tone="danger">{error}</Notice>}
    {success && <Notice>{success}</Notice>}
    {!detail && !error && <p className="loading-inline"><span className="spinner"/> {t('loading')}</p>}
    {detail && <>
      <Card className="section-card"><h2>{detail.identity.email}</h2>
        <p>{t('adminMembershipState', { state: t(stateKeys[detail.membership.state]) })}</p>
        <p>{detail.effectiveAccess ? t('adminMembershipEffectiveAccess') : t('adminMembershipNoEffectiveAccess')}</p>
        {detail.accessReasons.length > 0 && <ul>{detail.accessReasons.map((accessReason) => <li key={accessReason}>{t(accessKeys[accessReason])}</li>)}</ul>}
        <p>{t('adminMembershipOrigin', { origin: t(({ admin: 'adminMembershipOriginAdmin', invitation: 'adminMembershipOriginInvitation', admission: 'adminMembershipOriginAdmission', migration: 'adminMembershipOriginMigration' } as const)[detail.membership.origin]) })}</p>
        {detail.membership.stateReason && <p>{t('adminReason')}: {detail.membership.stateReason}</p>}
      </Card>
      <Card className="section-card"><h2>{t('adminRealmRoles')}</h2><p>{detail.roles.map((role) => role.name).join(', ') || t('adminNoItems')}</p><h2>{t('adminRealmGroups')}</h2><p>{detail.groups.map((group) => group.name).join(', ') || t('adminNoItems')}</p><h2>{t('adminMemberClientRoles')}</h2><p>{detail.clientRoles.map((role) => role.name).join(', ') || t('adminNoItems')}</p></Card>
      <Card className="section-card"><h2>{t('adminMemberFields')}</h2>{detail.fields.length ? <ul>{detail.fields.map((field) => <li key={field.fieldId}><strong>{field.label}</strong>: {field.hasValue ? JSON.stringify(field.value) : t('adminNoItems')}</li>)}</ul> : <p>{t('adminNoItems')}</p>}</Card>
      <Card className="section-card"><h2>{t('adminMemberChangeState')}</h2>
        {blocked && <Notice tone="warning" title={t('adminReadOnlyMode')}>{blocked}</Notice>}
        <Notice>{t('adminMemberStateScope')}</Notice>
        <form onSubmit={(event) => void save(event)}>
          <Field label={t('adminStatus')}><Select value={target} disabled={Boolean(blocked) || saving} onChange={(event) => setTarget(event.target.value as RealmMembershipState | '')} required><option value="">{t('adminMemberChooseState')}</option>{(['active', 'suspended', 'revoked'] as const).filter((value) => value !== detail.membership.state).map((value) => <option key={value} value={value}>{t(stateKeys[value])}</option>)}</Select></Field>
          <Field label={t('adminReason')}><Input value={reason} maxLength={500} required disabled={Boolean(blocked) || saving} onChange={(event) => setReason(event.target.value)}/></Field>
          <Button type="submit" disabled={Boolean(blocked) || saving || !target || !reason.trim()}>{t('adminMemberConfirmState')}</Button>
        </form>
      </Card>
    </>}
  </>;
}
